import { useEffect, useState } from "react";
import { SOCIAL_ICONS } from "./socialIcons";
import RichText from "./RichText";
import config from "./config";
import { cachedPost } from "./apiCache";

/**
 * Footer with Privacy Policy / Terms links (bottom-right).
 * Clicking a link opens the community's policy content in a modal.
 *
 * `showBranding` (driven by the community owner's branding add-on, via the API)
 * toggles the "Powered by FlexiFunnels" mark.
 */
// `communityName` is still passed by every caller; it is no longer read here
// (it only ever fed the copyright fallback), so it is not destructured.
function PolicyFooter({ policies, token, primary = "#0274F7", showBranding = false }) {
  const [open, setOpen] = useState(null); // "privacy" | "terms" | null
  // Bodies fetched on demand — see `body` below.
  const [fetched, setFetched] = useState(null);

  // The API empties the content when the owner turns a section off, so
  // non-empty content is what decides whether a link shows.
  //
  // Unless it told us separately. The Privacy and Terms bodies are ~28KB of
  // HTML that every page was downloading for a footer showing two links, so
  // /membership-groups now answers `skip_policies` with has_privacy/has_terms
  // and an empty body. When those flags are present they decide the links;
  // without them (an older response) the content still does, so this renders
  // correctly either way.
  const lazy = policies?.has_privacy !== undefined || policies?.has_terms !== undefined;
  const hasPrivacy = lazy
    ? !!policies.has_privacy
    : String(policies?.privacy || "").trim() !== "";
  const hasTerms = lazy
    ? !!policies.has_terms
    : String(policies?.terms || "").trim() !== "";
  // Titles are configurable in the admin's Footer settings.
  const privacyTitle = policies?.privacy_subject || "Privacy Policy";
  const termsTitle = policies?.terms_subject || "Terms";
  // Copyright line from the admin Footer tab — printed ONLY when the owner set
  // one. It used to fall back to "© <year> <community name>", which meant an
  // owner who deliberately left the field empty still got a copyright notice
  // they never wrote, with no way to remove it. Empty now means empty; the
  // footer keeps rendering for the policy links and social icons.
  const copyright = String(policies?.copyright || "").trim();
  const social = policies?.social || {};
  // `links` = every platform in the creator's order (flexiapi). A response from
  // before that list existed still carries the original three fields.
  const socials = (
    Array.isArray(social.links)
      ? social.links.map((l) => ({ key: l.platform, url: l.url }))
      : [
          { key: "facebook", url: social.facebook_url },
          { key: "x", url: social.x_url },
          { key: "instagram", url: social.instagram_url },
        ]
  )
    .filter((s) => SOCIAL_ICONS[s.key] && /^https?:\/\//i.test(String(s.url || "").trim()))
    .map((s) => ({ ...s, ...SOCIAL_ICONS[s.key] }));

  // Pull the bodies the first time a member opens either one. Cached, so the
  // second link — and a reopen — costs nothing.
  useEffect(() => {
    if (!open || !lazy || fetched || !token) return;
    let active = true;
    cachedPost(`${config.COMMUNITY_API_URL}/membership-groups`, {
        community_token: token,
        policies_only: 1,
      }, { ttl: 600000 })
      .then((res) => {
        if (active && res.data?.error === 0) setFetched(res.data.policies || {});
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open, lazy, fetched, token]);

  // What the modal shows: the fetched body once it lands, else whatever the
  // page already had (a non-lazy response carries it inline).
  const body = open ? (fetched || policies || {})[open] || "" : "";
  // A lazy footer with nothing fetched yet is loading, not empty — without this
  // the modal would flash "no content" for the length of the request.
  const bodyPending = lazy && !fetched;

  const hasLeft = copyright || hasPrivacy || hasTerms;
  const hasSocials = socials.length > 0;
  // Nothing configured at all → don't render an empty band.
  if (!hasLeft && !hasSocials) return null;
  // Center a single side; space them apart when both are present.
  const bothSides = hasLeft && hasSocials;

  return (
    <footer
      className="mt-auto border-t"
      style={{
        borderColor: `${primary}1f`,
        background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 45%, ${primary}05 100%)`,
      }}
    >
      <div
        className={`max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4 text-center sm:text-left sm:flex-row sm:items-center ${
          bothSides ? "sm:justify-between" : "sm:justify-center"
        }`}
      >
        {/* Left — copyright + policy links */}
        {hasLeft && (
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1.5 text-sm text-gray-600">
            {copyright && <span className="font-semibold text-gray-800">{copyright}</span>}
            {copyright && (hasPrivacy || hasTerms) && (
              <span className="text-gray-300 dark:text-gray-500 hidden sm:inline">|</span>
            )}
            {hasPrivacy && (
              <button
                onClick={() => setOpen("privacy")}
                className="font-medium transition-colors hover:opacity-80"
                style={{ color: primary }}
              >
                {privacyTitle}
              </button>
            )}
            {hasPrivacy && hasTerms && <span className="text-gray-300 dark:text-gray-500">|</span>}
            {hasTerms && (
              <button
                onClick={() => setOpen("terms")}
                className="font-medium transition-colors hover:opacity-80"
                style={{ color: primary }}
              >
                {termsTitle}
              </button>
            )}
          </div>
        )}

        {/* Right — social icons */}
        {hasSocials && (
          <div className="flex items-center justify-center gap-2.5">
            {socials.map((s) => (
              <a
                key={s.key}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="group/soc w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                style={{ color: primary, backgroundColor: `${primary}14`, boxShadow: `0 1px 2px ${primary}14` }}
                title={s.label}
                // Fills with the platform's own colour on hover — members
                // recognise the brand faster than the group colour.
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = s.color; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${primary}14`; }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-[18px] h-[18px] transition-colors duration-200 group-hover/soc:text-white"
                >
                  <path d={s.d} />
                </svg>
              </a>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center bg-ink/50 backdrop-blur-md p-0 sm:p-4 animate-[policyFade_.2s_ease-out]"
          /* No backdrop-click close — a stray click must not dismiss the policy
             mid-read. One overlay serves both Privacy and Terms (`open` holds
             which), so this covers both. Close via the X or Close. */
        >
          <style>{`
            @keyframes policyFade { from { opacity: 0 } to { opacity: 1 } }
            @keyframes policyPop { from { opacity: 0; transform: translateY(16px) scale(.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
          `}</style>
          <div
            className="relative bg-surface w-full h-full sm:h-auto sm:max-w-2xl rounded-none sm:rounded-3xl shadow-2xl max-h-none sm:max-h-[92vh] flex flex-col overflow-hidden sm:ring-1 sm:ring-black/5 animate-[policyPop_.28s_cubic-bezier(.22,1,.36,1)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Themed header — light brand-tinted gradient */}
            <div
              className="relative shrink-0 px-6 sm:px-7 pt-6 pb-5 overflow-hidden border-b border-black/5"
              style={{ background: `linear-gradient(135deg, ${primary}22 0%, ${primary}0d 55%, rgb(var(--c-surface)) 100%)` }}
            >
              {/* decorative glow */}
              <div
                className="pointer-events-none absolute -top-16 -right-10 w-52 h-52 rounded-full opacity-40 blur-2xl"
                style={{ background: `radial-gradient(circle, ${primary}55 0%, transparent 70%)` }}
              />
              {/* Close — pinned to top-right corner */}
              <button
                onClick={() => setOpen(null)}
                aria-label="Close"
                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-surface/70 hover:bg-surface text-gray-500 hover:text-gray-800 flex items-center justify-center shadow-sm ring-1 ring-black/5 transition"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="w-4 h-4">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              <div className="relative flex items-start gap-4 pr-10">
                <div
                  className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm"
                  style={{ backgroundColor: primary }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-white">
                    {open === "privacy" ? (
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    ) : (
                      <>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6M9 13h6M9 17h4" />
                      </>
                    )}
                  </svg>
                </div>
                <div className="min-w-0 pt-0.5">
                  <h3 className="text-lg font-bold text-gray-900 leading-tight truncate">
                    {open === "privacy" ? privacyTitle : termsTitle}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Please review the details below
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            {bodyPending ? (
              <div className="flex-1 min-h-0 px-6 sm:px-7 py-6 space-y-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-3.5 rounded bg-gray-100 animate-pulse"
                    style={{ width: `${[92, 100, 84, 96, 70][i]}%` }}
                  />
                ))}
              </div>
            ) : (
              <RichText
                html={body}
                className="flex-1 min-h-0 px-6 sm:px-7 py-6 overflow-y-auto text-[15px] text-gray-600 leading-relaxed space-y-4"
              />
            )}

            {/* Footer */}
            <div className="shrink-0 px-6 sm:px-7 py-4 border-t border-gray-100 bg-gray-50/70 flex items-center justify-end">
              <button
                onClick={() => setOpen(null)}
                style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                className="text-white text-sm font-semibold px-6 py-2.5 rounded-xl shadow-sm hover:opacity-90 hover:shadow-md active:scale-[.98] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}

export default PolicyFooter;
